"use client";

import {useCallback, useEffect, useRef, useState} from "react";
import {apiFetchWithStatus} from "@/lib/api";
import type {Mem0PublicItem} from "@/services/mem0/types";

export type {Mem0PublicItem};

type MemoryErrorBody = {
   error?: string;
};

type MemoryListBody = {
   items?: Mem0PublicItem[];
};

type MemoryItemBody = {
   item?: Mem0PublicItem;
};

export type MemoryImportFailure = {
   text: string;
   error: string;
};

export type MemoryImportResult = {
   saved: string[];
   failed: MemoryImportFailure[];
   extracted: number;
};

function errorKeyFromBody(data: unknown, fallback = "memory.requestFailed"): string {
   if (typeof data === "object" && data != null && "error" in data) {
      const error = (data as MemoryErrorBody).error;
      if (typeof error === "string" && error.startsWith("memory.")) {
         return error;
      }
   }
   return fallback;
}

export function useGailGptMemories(open: boolean) {
   const [items, setItems] = useState<Mem0PublicItem[]>([]);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [mutating, setMutating] = useState(false);
   const [importing, setImporting] = useState(false);
   // Incremented per load and on close/unmount; a superseded response is dropped
   // so a slow earlier request can't overwrite a freshly loaded list.
   const loadGeneration = useRef(0);

   const load = useCallback(async (opts?: {silent?: boolean}) => {
      const generation = ++loadGeneration.current;
      if (!opts?.silent) {
         setLoading(true);
      }
      setError(null);
      const response = await apiFetchWithStatus<MemoryListBody>("/api/gailgpt/memories", "GET");
      if (generation !== loadGeneration.current) {
         return;
      }
      if (!response.success) {
         if (!opts?.silent) {
            setItems([]);
         }
         setError(errorKeyFromBody(response.data, "memory.requestFailed"));
         setLoading(false);
         return;
      }
      setItems(Array.isArray(response.data?.items) ? response.data.items : []);
      setLoading(false);
   }, []);

   useEffect(() => {
      if (!open) return;
      void load();
      return () => {
         loadGeneration.current += 1;
      };
   }, [open, load]);

   const add = useCallback(async (text: string): Promise<boolean> => {
      setMutating(true);
      setError(null);
      try {
         const response = await apiFetchWithStatus<MemoryListBody>("/api/gailgpt/memories", "POST", {
            body: {text},
         });
         if (!response.success) {
            setError(errorKeyFromBody(response.data, "memory.requestFailed"));
            return false;
         }
         await load({silent: true});
         return true;
      } finally {
         setMutating(false);
      }
   }, [load]);

   const update = useCallback(async (id: string, text: string): Promise<boolean> => {
      setMutating(true);
      setError(null);
      try {
         const response = await apiFetchWithStatus<MemoryItemBody>(
            `/api/gailgpt/memories/${encodeURIComponent(id)}`,
            "PATCH",
            {body: {text}}
         );
         if (!response.success) {
            setError(errorKeyFromBody(response.data, "memory.requestFailed"));
            return false;
         }
         await load({silent: true});
         return true;
      } finally {
         setMutating(false);
      }
   }, [load]);

   const remove = useCallback(async (id: string): Promise<boolean> => {
      setMutating(true);
      setError(null);
      try {
         const response = await apiFetchWithStatus(`/api/gailgpt/memories/${encodeURIComponent(id)}`, "DELETE");
         if (!response.success) {
            setError(errorKeyFromBody(response.data, "memory.requestFailed"));
            return false;
         }
         await load({silent: true});
         return true;
      } finally {
         setMutating(false);
      }
   }, [load]);

   const importMemories = useCallback(
      async (text: string): Promise<MemoryImportResult | null> => {
         setImporting(true);
         setError(null);
         try {
            const response = await apiFetchWithStatus<MemoryImportResult>(
               "/api/gailgpt/memories/import",
               "POST",
               {body: {text}}
            );
            if (!response.success) {
               setError(errorKeyFromBody(response.data, "memory.requestFailed"));
               return null;
            }
            const result: MemoryImportResult = {
               saved: Array.isArray(response.data?.saved) ? response.data.saved : [],
               failed: Array.isArray(response.data?.failed) ? response.data.failed : [],
               extracted:
                  typeof response.data?.extracted === "number" ? response.data.extracted : 0,
            };
            if (result.saved.length > 0) {
               await load({silent: true});
            }
            return result;
         } finally {
            setImporting(false);
         }
      },
      [load]
   );

   return {items, loading, error, mutating, importing, add, update, remove, importMemories};
}
